import React, { FC, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    ListItemText,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Loader from 'components/shared/loader/loader.component';
import { organizationUsersForImpersonationTemplate } from 'apollo/templates/orders';
import { getImpersonateUserTokenTemplate } from 'apollo/templates/user/query.graphql';
import { openImpersonationSession } from 'utils/open-impersonation-session';
import { AdminOrderOrganization } from 'models/admin-order';

type OrganizationUser = { _id: string; fullName?: string; email?: string; role?: string };

type Props = {
    organization: AdminOrderOrganization;
    /** Regular-app path to open, e.g. the order or invoice the admin is looking at. */
    path?: string;
    onClose: () => void;
};

/** Pick an active user of `organization` and open the regular app as them, in that organization. */
const LogInAsOrganizationUserDialog: FC<Props> = ({ organization, path, onClose }) => {
    const { t: translate } = useTranslation('super-admin');
    const [errorMessage, setErrorMessage] = useState<string>();
    const { data, loading } = useQuery<{ users: OrganizationUser[] }>(organizationUsersForImpersonationTemplate, {
        variables: { orgId: organization._id },
        fetchPolicy: 'network-only',
    });
    const [impersonate, { loading: impersonating }] = useLazyQuery(getImpersonateUserTokenTemplate, {
        fetchPolicy: 'network-only',
        onCompleted: ({ impersonateUser }) => {
            openImpersonationSession(impersonateUser.token, undefined, path);
            onClose();
        },
        onError: (error) => setErrorMessage(error.message),
    });

    const users = data?.users ?? [];

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{translate('LOG_IN_AS_IN_ORGANIZATION', { name: organization.name })}</DialogTitle>
            <DialogContent>
                {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : undefined}
                {loading || impersonating ? <Loader /> : undefined}
                {!loading && users.length === 0 ? (
                    <Typography variant="body2">{translate('NO_ACTIVE_USERS')}</Typography>
                ) : undefined}
                <List dense>
                    {users.map((user) => (
                        <ListItemButton
                            key={user._id}
                            disabled={impersonating}
                            onClick={() =>
                                impersonate({ variables: { userId: user._id, organizationId: organization._id } })
                            }
                        >
                            <ListItemText primary={user.fullName ?? user.email} secondary={user.email} />
                        </ListItemButton>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button variant="text" onClick={onClose}>
                    {translate('CLOSE')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LogInAsOrganizationUserDialog;
